// src/components/admin/UserLimitsDashboard.js
import React, { useState } from 'react';
import { subscriptionRequestService } from '../../services/apiServices';
import { showErrorToast, showLoadingToast, updateToast } from '../../utils/toastUtils';

const UserLimitsDashboard = ({ currentUser, onRequestSubmitted, onRefreshLimits, companyQuotas, companyUsage }) => {
  // ponytail: onRefreshLimits kept unused for 1 release to avoid touching AdminUserManagement caller
  void onRefreshLimits;
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestData, setRequestData] = useState({
    requestedUsers: '',
    requestedRole: 'simpleuser',
    justification: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Company-level quotas are the sole source of truth (set by superadmin per enterprise).
  // null/undefined/'' = unlimited. Always render so empty quotas show ∞, never a stale 0.
  const quotas = companyQuotas || {};
  const fmtQuota = (v) => (v === null || v === undefined || v === '' ? '∞' : v);

  const handleRequestInputChange = (e) => {
    const { name, value } = e.target;
    setRequestData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRequestSubmit = async (e) => {
    e.preventDefault();

    if (!requestData.requestedUsers || !requestData.justification.trim()) {
      showErrorToast('Veuillez remplir tous les champs requis');
      return;
    }

    const requestedCount = parseInt(requestData.requestedUsers);
    if (isNaN(requestedCount) || requestedCount <= 0) {
      showErrorToast('Le nombre d\'utilisateurs demandés doit être un nombre positif');
      return;
    }

    const loadingToastId = showLoadingToast('Envoi de la demande en cours...');
    setIsSubmitting(true);

    try {
      const response = await subscriptionRequestService.createRequest({
        type: 'user_limit_increase',
        requestType: 'user_limit_increase',
        requestedUsers: requestedCount,
        requestedRole: requestData.requestedRole || 'simpleuser',
        currentLimit: null,
        currentUsage: null,
        justification: requestData.justification.trim(),
        message: `Quotas actuels — admins: ${companyUsage?.admins ?? '-'}/${fmtQuota(quotas.maxAdmins)}, analystes: ${companyUsage?.analysts ?? '-'}/${fmtQuota(quotas.maxSimpleUsers)}, lecteurs: ${companyUsage?.viewers ?? '-'}/${fmtQuota(quotas.maxViewers)}`,
        companyId: currentUser.companyId,
        companyName: currentUser.companyName
      });

      if (response.success) {
        updateToast(loadingToastId, 'Demande envoyée avec succès', 'success');
        setShowRequestModal(false);
        setRequestData({ requestedUsers: '', requestedRole: 'simpleuser', justification: '' });
        if (onRequestSubmitted) {
          onRequestSubmitted();
        }
      } else {
        throw new Error(response.message || 'Erreur lors de l\'envoi de la demande');
      }
    } catch (error) {
      console.error('Request submission error:', error);
      updateToast(loadingToastId, error.message || 'Erreur lors de l\'envoi de la demande', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-6 mb-6">
          <h3 className="text-lg font-semibold text-purple-800 mb-2">
            Quota entreprise
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="bg-white rounded-md p-3 border border-purple-100">
              <div className="text-gray-500">Admins</div>
              <div className="text-xl font-bold text-gray-900">
                {companyUsage?.admins ?? '-'}<span className="text-sm font-normal text-gray-500">/{fmtQuota(quotas.maxAdmins)}</span>
              </div>
            </div>
            <div className="bg-white rounded-md p-3 border border-purple-100">
              <div className="text-gray-500">Analystes</div>
              <div className="text-xl font-bold text-gray-900">
                {companyUsage?.analysts ?? '-'}<span className="text-sm font-normal text-gray-500">/{fmtQuota(quotas.maxSimpleUsers)}</span>
              </div>
            </div>
            <div className="bg-white rounded-md p-3 border border-purple-100">
              <div className="text-gray-500">Lecteurs</div>
              <div className="text-xl font-bold text-gray-900">
                {companyUsage?.viewers ?? '-'}<span className="text-sm font-normal text-gray-500">/{fmtQuota(quotas.maxViewers)}</span>
              </div>
            </div>
          </div>
          <p className="text-xs text-purple-700 mt-3">Quota fixé par le superadmin. Création bloquée au-delà du quota (demandez une augmentation via le bouton ci-dessous).</p>
          <div className="flex items-center space-x-3 pt-3">
            <button
              onClick={() => setShowRequestModal(true)}
              className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-colors text-sm font-medium"
              disabled={isSubmitting}
            >
              Demander une Augmentation
            </button>
          </div>
        </div>

      {/* Request Modal */}
      {showRequestModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Demander Plus d'Utilisateurs
                </h3>
                <button
                  onClick={() => setShowRequestModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                  disabled={isSubmitting}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <form onSubmit={handleRequestSubmit} className="space-y-4">
                {/* Current Status */}
                <div className="bg-gray-50 p-3 rounded-md text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Admins:</span>
                    <span className="font-medium">{companyUsage?.admins ?? '-'}/{fmtQuota(quotas.maxAdmins)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Analystes:</span>
                    <span className="font-medium">{companyUsage?.analysts ?? '-'}/{fmtQuota(quotas.maxSimpleUsers)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Lecteurs:</span>
                    <span className="font-medium">{companyUsage?.viewers ?? '-'}/{fmtQuota(quotas.maxViewers)}</span>
                  </div>
                </div>

                {/* Requested Role */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type d'utilisateurs *
                  </label>
                  <select
                    name="requestedRole"
                    value={requestData.requestedRole}
                    onChange={handleRequestInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                    disabled={isSubmitting}
                  >
                    <option value="admin">Admins</option>
                    <option value="simpleuser">Analystes</option>
                    <option value="viewer">Lecteurs</option>
                  </select>
                </div>

                {/* Requested Users */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nombre d'utilisateurs supplémentaires demandés *
                  </label>
                  <input
                    type="number"
                    name="requestedUsers"
                    value={requestData.requestedUsers}
                    onChange={handleRequestInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Ex: 5"
                    min="1"
                    required
                    disabled={isSubmitting}
                  />
                </div>

                {/* Justification */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Justification de la demande *
                  </label>
                  <textarea
                    name="justification"
                    value={requestData.justification}
                    onChange={handleRequestInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="4"
                    placeholder="Expliquez pourquoi vous avez besoin d'utilisateurs supplémentaires..."
                    required
                    disabled={isSubmitting}
                  />
                </div>

                {/* Buttons */}
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowRequestModal(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
                    disabled={isSubmitting}
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors disabled:opacity-50"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Envoi...' : 'Envoyer la Demande'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UserLimitsDashboard;